import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { track } from "@databuddy/sdk";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/components/Toast";
import { Alert, Card, FormCard, Tabs } from "@/components/ui";
import { useSession } from "@/contexts/AuthContext";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { useHousehold } from "@/hooks/useHousehold";
import { usePopulateHouseholdForm } from "@/hooks/usePopulateHouseholdForm";
import { useCalculatorQueryParams } from "@/hooks/useQueryParamsInit";
import { useSaveWithConfirm } from "@/hooks/useSaveWithConfirm";
import { INDEXABLE_ROUTE_META } from "@/seo/routeMeta";
import {
  CompactTaxYearSelector,
  ConfirmationModal,
  HouseholdFormFields,
  LoginPromptModal,
  PageLayout,
  StrategyCard,
  Warnings,
} from "../components";
import { FeatureHighlights, FAQSection, FAQSchema } from "../components/seo";
import { useHouseholdReport } from "../hooks/useHouseholdReport";
import { useHouseholdState } from "../hooks/useHouseholdState";
import { strategyLabels } from "../labels/strategyLabels";
import type { FAQItem } from "../components/seo";
import type { StrategyName, StrategyResult } from "../types";

const CALCULATOR_FAQS: FAQItem[] = [
  {
    question: 'How much tax will I pay on my salary?',
    answer:
      'Enter your salary in the calculator above to see a detailed breakdown of Income Tax, National Insurance, and your take-home pay. Our calculator shows you exactly how much goes to each tax band, plus optimization strategies to reduce your bill.',
  },
  {
    question: 'What is the 60% tax trap?',
    answer:
      'The 60% tax trap affects earners between £100,000 and £125,140. In this band, you lose £1 of personal allowance for every £2 earned, creating an effective 60% marginal tax rate. Our calculator detects when you\'re in this trap and shows you how to escape it.',
  },
  {
    question: 'How much pension do I need to avoid the £100k tax trap?',
    answer:
      'The amount depends on your total income. Use our calculator to see the exact pension contribution needed to bring your adjusted net income below £100,000 and restore your full personal allowance. We show specific £ amounts for your situation.',
  },
  {
    question: 'Will I lose my free childcare hours if I earn over £100k?',
    answer:
      'Yes — if your adjusted net income exceeds £100,000, you lose eligibility for 30 hours free childcare and Tax-Free Childcare (worth up to £23,300/year). Our calculator shows how pension contributions can bring you back under the threshold.',
  },
  {
    question: 'How do I beat the 60% tax trap?',
    answer:
      'The most effective ways are: pension contributions (reduces adjusted net income), salary sacrifice arrangements, or charitable donations via Gift Aid. Our \'Escape £100k Trap\' strategy calculates the exact amounts needed for your situation.',
  },
];

export function CalculatorPage(): React.JSX.Element {
  useDocumentMeta(INDEXABLE_ROUTE_META['/calculator']);

  const { user } = useSession();
  const { household, loading: isLoadingHousehold, saveHousehold } = useHousehold();
  const { show: showToast } = useToast();
  const navigate = useNavigate();
  const householdState = useHouseholdState();
  const {
    warnings,
    hasReport,
    preferredStrategy,
    setPreferredStrategy,
    taxYear,
    updateFromReport,
    getHouseholdInput,
  } = householdState;
  const { report, loading, error, submit } = useHouseholdReport();
  const {
    showConfirmModal,
    handleSubmit: handleSubmitWithConfirm,
    handleConfirmUpdate,
    handleCancelUpdate,
  } = useSaveWithConfirm({ user, household, isLoadingHousehold, saveHousehold, submit });
  const [showSwitchPrompt, setShowSwitchPrompt] = useState(false);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [hasAutoCalculated, setHasAutoCalculated] = useState(false);

  useEffect(() => {
    updateFromReport(report);
  }, [report, updateFromReport]);

  const trackResult = useCallback((result: { success: boolean; error?: string } | undefined): void => {
    if (!result) return;

    if (result.success) {
      track('calculation_succeeded', { page: 'calculator' });
    } else {
      track('calculation_failed', { page: 'calculator', reason: result.error ?? 'unknown' });
    }
  }, []);

  // Mobile tab state
  const [activeTab, setActiveTab] = useState<'input' | 'results'>('input');

  // Track if we're on mobile to prevent rendering charts when hidden
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = (): void => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Populate form from saved household once (wrap preferred strategy to accept undefined)
  const hasAppliedHousehold = usePopulateHouseholdForm(household, {
    setPrimary: householdState.setPrimary,
    setPartner: householdState.setPartner,
    setIncludePartner: householdState.setIncludePartner,
    setChildren: householdState.setChildren,
    setClaimsChildBenefit: householdState.setClaimsChildBenefit,
    setPreferredStrategy: (s) => householdState.setPreferredStrategy(s ?? null),
  });

  // Parse and apply query parameters (highest priority - runs once after page load)
  // Use state (not ref) so auto-calc effect re-runs after query params are applied
  const queryParams = useCalculatorQueryParams();
  const hasAppliedQueryParamsRef = useRef(false);
  const [hasAppliedQueryParams, setHasAppliedQueryParams] = useState(false);

  // Destructure setters to avoid effect re-running on every render
  // (householdState returns a new object each render)
  const {
    setPrimary,
    setChildren,
    setClaimsChildBenefit,
    setIncludePartner,
    setPartner,
  } = householdState;

  useEffect(() => {
    // Skip if already applied or still loading household
    if (hasAppliedQueryParamsRef.current) return;
    if (isLoadingHousehold) return;
    // Wait for household to be applied if user is logged in and has data
    if (user && household && !hasAppliedHousehold) return;

    hasAppliedQueryParamsRef.current = true;

    // Apply primary person fields
    if (Object.keys(queryParams.primary).length > 0) {
      setPrimary((prev) => ({ ...prev, ...queryParams.primary }));
    }

    // Apply children
    if (queryParams.children && queryParams.children.length > 0) {
      setChildren(queryParams.children);
    }

    // Apply child benefit
    if (queryParams.claimsChildBenefit !== undefined) {
      setClaimsChildBenefit(queryParams.claimsChildBenefit);
    }

    // Apply partner inclusion
    if (queryParams.includePartner !== undefined) {
      setIncludePartner(queryParams.includePartner);
    }

    // Apply partner fields
    if (queryParams.partner && Object.keys(queryParams.partner).length > 0) {
      setPartner((prev) => ({ ...prev, ...queryParams.partner }));
    }

    // Signal that query params are applied (triggers re-render so auto-calc sees updated state)
    setHasAppliedQueryParams(true);
  }, [
    queryParams,
    isLoadingHousehold,
    user,
    household,
    hasAppliedHousehold,
    setPrimary,
    setChildren,
    setClaimsChildBenefit,
    setIncludePartner,
    setPartner,
  ]);

  // Auto-calculate for logged-in users once their data is loaded/applied
  useEffect(() => {
    if (isLoadingHousehold || hasAutoCalculated) {
      return;
    }

    if (household && !hasAppliedHousehold) {
      return;
    }

    // Wait for query params to be applied so URL overrides are respected
    if (!hasAppliedQueryParams) {
      return;
    }

    if (!user) {
      return;
    }

    const payload = getHouseholdInput();
    setHasAutoCalculated(true);
    track('calculation_attempted', { page: 'calculator', source: 'auto' });
    void submit(payload).then(trackResult);
  }, [
    getHouseholdInput,
    hasAppliedHousehold,
    hasAppliedQueryParams,
    hasAutoCalculated,
    household,
    isLoadingHousehold,
    user,
    submit,
    trackResult,
  ]);

  useEffect(() => {
    setHasAutoCalculated(false);
  }, [household]);

  const handleSubmit = async (e?: React.FormEvent): Promise<void> => {
    e?.preventDefault();
    const input = getHouseholdInput();
    track('calculation_attempted', { page: 'calculator', source: 'manual' });
    const result = await handleSubmitWithConfirm(input);
    trackResult(result);
    // Switch to results tab on mobile after submission
    if (isMobile) {
      setActiveTab('results');
    }
  };

  const handleConfirmModalConfirm = useCallback(async (): Promise<void> => {
    const result = await handleConfirmUpdate();
    trackResult(result);
  }, [handleConfirmUpdate, trackResult]);

  const handleConfirmModalCancel = useCallback(async (): Promise<void> => {
    const result = await handleCancelUpdate();
    trackResult(result);
  }, [handleCancelUpdate, trackResult]);

  const baselineStrategy = useMemo(() => {
    if (!report) return null;
    // Baseline represents user's CURRENT contributions, not a strategy
    // Use null for name since "Baseline" title is set explicitly in UI
    return {
      name: null,
      recommended_pension: report.pension_contributions_grossed_up,
      recommended_charity: report.charity_gross_donation,
      cash_in_hand: report.cash_in_hand,
      total_tax: report.actual_tax,
      national_insurance: report.national_insurance,
      taxable_benefits: report.taxable_benefits,
      suggested_pension: 0,
      suggested_charity: 0,
      projected_tax_savings_vs_max_cash: 0,
      free_childcare: report.free_childcare,
      tax_free_childcare: report.tax_free_childcare,
      child_benefit_repayment: report.child_benefit_repayment,
      description: "Your current setup without applying any optimisation strategies.",
    };
  }, [report]);

  // Filter strategies to ensure they all have valid names (backend strategies always do)
  // This type guard helps TypeScript know these strategies have non-null names
  const strategies = useMemo(
    () => (report?.strategies ?? []).filter((s): s is StrategyResult & { name: StrategyName } => s.name !== null),
    [report]
  );
  const availableStrategyNames = useMemo(
    () => new Set<StrategyName>(strategies.map((strategy) => strategy.name)),
    [strategies]
  );

  const preferredStrategyUnavailable =
    report !== null &&
    preferredStrategy !== null &&
    !availableStrategyNames.has(preferredStrategy);

  const handleSelectStrategy = useCallback(
    async (name: StrategyName): Promise<void> => {
      setPreferredStrategy(name);

      const payload = getHouseholdInput();
      const payloadWithStrategy = {
        ...payload,
        preferred_strategy: name,
      };

      try {
        if (user) {
          await saveHousehold(payloadWithStrategy);
          setShowSwitchPrompt(true);
        } else {
          setShowLoginPrompt(true);
        }
      } catch (err) {
        showToast({
          variant: "error",
          title: "Failed to save preference",
          description: err instanceof Error ? err.message : "Please try again.",
        });
      }
    },
    [getHouseholdInput, saveHousehold, setPreferredStrategy, showToast, user]
  );

  // confirm handlers provided by hook

  return (
    <PageLayout maxWidth="full">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-2xl md:text-3xl font-bold mb-4">UK Salary Tax Calculator {taxYear.replace('-', '/')}</h1>

        {/* Mobile Tabs */}
        <Tabs
          tabs={[
            { id: 'input', label: 'Input' },
            { id: 'results', label: 'Results' },
          ]}
          activeTab={activeTab}
          onTabChange={(tabId) => setActiveTab(tabId as 'input' | 'results')}
          className="md:hidden mb-4"
        />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Left Column - Form */}
          <div className={`lg:col-span-5 ${activeTab === 'results' ? 'hidden md:block' : 'block'} relative space-y-4`}>
            <CompactTaxYearSelector
              taxYear={householdState.taxYear}
              onTaxYearChange={householdState.setTaxYear}
            />
            <FormCard
              title="Household Details"
              buttonText="Calculate"
              loading={loading}
              onSubmit={handleSubmit}
            >
              <HouseholdFormFields mode="full" householdState={householdState} />
            </FormCard>
          </div>

          {/* Right Column - Results */}
          <div className={`lg:col-span-7 ${activeTab === 'input' ? 'hidden md:block' : 'block'}`}>
            {/* Only render content when visible on mobile, or always on desktop */}
            {(!isMobile || activeTab === 'results') && (
              <div className="space-y-6">
                {error && (
                  <Alert variant="error" title="Error">
                    {error.message}
                  </Alert>
                )}

                {hasReport && (
                  <div className="space-y-6">
                    {baselineStrategy && (
                      <StrategyCard
                        strategy={baselineStrategy}
                        title="Baseline"
                      />
                    )}
                    <Warnings warnings={warnings} />

                    <div className="border-t border-gray-200 pt-6 space-y-6">
                      <div className="space-y-2">
                        <h2 className="text-xl font-semibold">Tax Optimisation Strategies</h2>
                        <p className="text-gray-600">
                          Compare available strategies against your baseline to pick a preferred approach.
                          Each strategy shows different ways to optimise your tax position by adjusting pension
                          and charity contributions.
                        </p>
                      </div>

                      {preferredStrategyUnavailable && (
                        <Alert variant="warning" title="Strategy Unavailable">
                          Your saved preferred strategy is not available for these results. Please select one of the
                          strategies below to update your preference.
                        </Alert>
                      )}

                      {strategies.length === 0 ? (
                        <Card padding="lg">
                          <p className="text-gray-600">
                            No optimisation strategies available for your current setup.
                          </p>
                        </Card>
                      ) : (
                        <div className="space-y-6">
                          {strategies.map((strategy) => {
                            const isSelected = preferredStrategy === strategy.name;
                            return (
                              <StrategyCard
                                key={strategy.name}
                                strategy={strategy}
                                title={strategyLabels[strategy.name] ?? strategy.name}
                                isSelected={isSelected}
                                onSelect={() => { void handleSelectStrategy(strategy.name); }}
                              />
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {!hasReport && !error && !loading && (
                  <Card padding="lg" className="text-center text-gray-500">
                    Enter your details and click Calculate to see your baseline and optimisation strategies
                  </Card>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="mt-8">
          <FeatureHighlights
            features={[
              'See your exact tax trap threshold',
              'Compare 4 optimization strategies side-by-side',
              'Include dividend & investment income',
              'Protect your childcare benefits',
              'Get specific pension contribution targets',
            ]}
          />
        </div>

        <FAQSection items={CALCULATOR_FAQS} />
        <FAQSchema items={CALCULATOR_FAQS} />
      </div>

      <ConfirmationModal
        isOpen={showConfirmModal}
        title="Update Saved Household Settings?"
        message="You've changed your household information. Do you want to update your saved settings?"
        confirmText="Save & Calculate"
        cancelText="Just Calculate"
        onConfirm={handleConfirmModalConfirm}
        onCancel={handleConfirmModalCancel}
      />

      <ConfirmationModal
        isOpen={showSwitchPrompt}
        title="Switch to Tracker?"
        message="Your preferred strategy was saved. Do you want to switch to the tracker to start tracking progress?"
        confirmText="Go to Tracker"
        cancelText="Stay Here"
        onConfirm={() => { setShowSwitchPrompt(false); navigate('/tracker'); }}
        onCancel={() => setShowSwitchPrompt(false)}
      />

      <LoginPromptModal
        isOpen={showLoginPrompt}
        onClose={() => setShowLoginPrompt(false)}
        title="Sign in to save your strategy"
        message="Sign in to save your preferred strategy and track your progress over time. Your data will be securely stored and accessible from any device."
      />
    </PageLayout>
  );
}

export default CalculatorPage;
